import { navigatorClient } from "@/api";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  endSession,
  renewAccessToken,
} from "@/api/auth-tokens";
import { getApiStatus } from "@/lib/api-error";
import { API_BASE_URL, API_PREFIX } from "@/constants";
import type { ChatMessage } from "./chat-service";
import type { AssistantAgent } from "@/pages/chat/components/assistant-results";
import type { ConsultationFeedback } from "@/pages/chat/components/consultation-dialogs";

export interface Need {
  work: string;
  needs: string;
  bottleneck: string;
  goal: string;
  environment: string[];
  environment_status?: "known" | "unknown" | "unrestricted";
  as_is?: string;
  integration?: string;
}
export interface CollectedFact {
  quote: string;
  status: "known" | "unknown" | "unrestricted";
}
export interface ExtractedInsight {
  summaries?: Record<string, string>;
  needs: Need[];
  scope: string;
  unknown: string[];
  requirements: { quote: string; mandatory: boolean }[];
  company_size?: string;
  company_size_status?: "known" | "unknown";
  industry?: CollectedFact;
  deployment?: CollectedFact;
  security?: CollectedFact;
  adoption_method?: string;
  adoption_conditions?: string;
}
export interface AssistantSession {
  id: string;
  title: string;
  updated_at: string;
  revision: number;
  feedback_due: boolean;
  processing: boolean;
  retry: { request_id: string; message: string } | null;
  messages: ChatMessage[];
  state: {
    required_complete?: boolean;
    access_changed?: boolean;
    insight?: ExtractedInsight;
    interests?: { id: string; name: string }[];
    results?: {
      status: "waiting" | "ready" | "error";
      degraded?: boolean;
      suitable: AssistantAgent[];
      exploratory: AssistantAgent[];
    };
    demo?: { id: string; name: string; url: string | null };
  };
}
export type HistoryItem = Pick<AssistantSession, "id" | "title" | "updated_at">;
export const assistantApi = {
  async create(request_id: string, agent_id?: string, case_study_id?: string) {
    return (
      await navigatorClient.post<{ data: AssistantSession }>("/chat/sessions", {
        request_id,
        agent_id,
        case_study_id,
      })
    ).data.data;
  },
  async get(id: string, signal?: AbortSignal) {
    return (
      await navigatorClient.get<{ data: AssistantSession }>(
        `/chat/sessions/${id}`,
        { signal },
      )
    ).data.data;
  },
  async history(signal?: AbortSignal) {
    const { data } = await navigatorClient.get<{
      data: HistoryItem[];
      meta?: { history_months: number; input_max_bytes?: number };
    }>("/chat/sessions", { signal });
    return {
      items: data.data,
      history_months: data.meta?.history_months ?? 6,
      input_max_bytes: data.meta?.input_max_bytes ?? 4096,
    };
  },
  async hide(id: string) {
    await navigatorClient.delete(`/chat/sessions/${id}`);
  },
  async feedback(id: string, revision: number, feedback: ConsultationFeedback) {
    await navigatorClient.post(`/chat/sessions/${id}/feedback`, {
      revision,
      ...feedback,
    });
  },
};

export class AssistantError extends Error {
  constructor(
    message: string,
    public code = "SERVICE_UNAVAILABLE",
    public retryAt: number | null = null,
  ) {
    super(message);
  }
}

export async function streamTurn(
  id: string,
  request: { request_id: string; message: string },
  signal: AbortSignal,
  onEvent: (kind: string, data: unknown) => void,
) {
  const send = () =>
    fetch(`${API_BASE_URL}${API_PREFIX}/chat/sessions/${id}/turns`, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY) ?? ""}`,
      },
      body: JSON.stringify(request),
    });
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  let response = await send();
  if (response.status === 401 && token) {
    try {
      await renewAccessToken(token);
    } catch (error) {
      if (
        [401, 403].includes(getApiStatus(error) ?? 0) ||
        !localStorage.getItem(REFRESH_TOKEN_KEY)
      ) {
        endSession();
        throw new AssistantError(
          "로그인이 만료되었습니다. 다시 로그인해 주세요.",
          "UNAUTHORIZED",
        );
      }
      throw new AssistantError(
        "로그인 상태를 확인하지 못했습니다. 연결 상태를 확인하고 다시 시도해 주세요.",
      );
    }
    response = await send();
  }
  if (response.status === 401) {
    endSession();
    throw new AssistantError(
      "로그인이 만료되었습니다. 다시 로그인해 주세요.",
      "UNAUTHORIZED",
    );
  }
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => ({}));
    const error = body.error ?? {};
    const retry = error.details?.[0]?.retry_at;
    throw new AssistantError(
      error.message ?? "메시지를 전송하지 못했습니다.",
      error.code,
      retry ? Date.parse(retry) : null,
    );
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "",
    done = false;
  try {
    for (;;) {
      const part = await reader.read();
      buffer += decoder.decode(part.value, { stream: !part.done });
      let boundary: number;
      while ((boundary = buffer.indexOf("\n\n")) >= 0) {
        const packet = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const kind = packet
          .split("\n")
          .find((l) => l.startsWith("event: "))
          ?.slice(7);
        const raw = packet
          .split("\n")
          .filter((l) => l.startsWith("data: "))
          .map((l) => l.slice(6))
          .join("\n");
        if (!kind || !raw) continue;
        const data = JSON.parse(raw);
        if (kind === "error") throw new AssistantError(data.message, data.code);
        if (kind === "done") done = true;
        onEvent(kind, data);
      }
      if (part.done) break;
    }
    if (!done)
      throw new AssistantError(
        "연결이 끊겨 답변이 완료되지 않았어요. 다시 시도하거나 새 메시지를 보내 주세요.",
        "CONNECTION_INTERRUPTED",
      );
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
